import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Collapse } from '@mui/material';
import { WarningAmberRounded, ErrorOutlineRounded, CloseRounded, ArrowForwardRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * RenewalBanner
 *
 * Props:
 *   variant      'expiring' | 'expired'
 *   daysLeft     number  (only relevant when variant='expiring')
 *   expiresAt    number  (epoch ms)
 */
export default function RenewalBanner({ variant, daysLeft, expiresAt }) {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const formatDate = (ms) =>
    ms
      ? new Date(ms).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';

  const isExpired = variant === 'expired';

  const bgGradient = isExpired
    ? 'linear-gradient(135deg, rgba(211,47,47,0.15), rgba(183,28,28,0.08))'
    : 'linear-gradient(135deg, rgba(237,108,2,0.15), rgba(230,81,0,0.08))';

  const borderColor = isExpired ? 'rgba(244,67,54,0.45)' : 'rgba(255,152,0,0.45)';
  const iconColor   = isExpired ? '#ef5350'               : '#ffa726';
  const btnBg       = isExpired
    ? 'linear-gradient(135deg, #ef5350, #b71c1c)'
    : 'linear-gradient(135deg, #ffa726, #e65100)';

  const message = isExpired
    ? `Your Premium subscription expired on ${formatDate(expiresAt)}. Premium features are now disabled.`
    : daysLeft === 0
      ? 'Your Premium subscription expires today! Renew now to avoid interruption.'
      : `Your Premium subscription expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} on ${formatDate(expiresAt)}.`;

  const btnLabel = isExpired ? 'Re-subscribe' : 'Renew Now';

  return (
    <Collapse in={!dismissed} unmountOnExit>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          background: bgGradient,
          border: `1px solid ${borderColor}`,
          borderRadius: 0,
          position: 'relative',
        }}
      >
        {/* Icon */}
        {isExpired
          ? <ErrorOutlineRounded sx={{ color: iconColor, fontSize: 20, flexShrink: 0 }} />
          : <WarningAmberRounded sx={{ color: iconColor, fontSize: 20, flexShrink: 0 }} />
        }

        {/* Message */}
        <Typography
          sx={{
            flex: 1,
            fontSize: 13,
            fontFamily: 'Inter, sans-serif',
            color: 'var(--text-primary)',
            lineHeight: 1.4,
          }}
        >
          {message}
        </Typography>

        {/* CTA */}
        <Button
          size="small"
          onClick={() => navigate('/subscription')}
          endIcon={<ArrowForwardRounded sx={{ fontSize: 14 }} />}
          sx={{
            flexShrink: 0,
            background: btnBg,
            color: '#fff',
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 12,
            px: 1.5,
            py: 0.5,
            boxShadow: 'none',
            '&:hover': { opacity: 0.88, boxShadow: 'none' },
          }}
        >
          {btnLabel}
        </Button>

        {/* Dismiss */}
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          sx={{ color: 'var(--text-muted)', flexShrink: 0 }}
          aria-label="Dismiss renewal banner"
        >
          <CloseRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Collapse>
  );
}
